import { useState } from "react";
import { analyzeRecipe } from "../lib/gemini";
import { useAuth } from "../lib/AuthContext";
import { useUserStats } from "../lib/UserStatsContext";
import { useBadgeToast } from "./BadgeToast";
import { AIContent } from "./GIBadge";
import { getUserStats, recordRecipeAnalysis, checkAndAwardBadges, checkUsageLimit, recordFeatureUsage } from "../lib/userStats";
import BloodSugarGraph from "./BloodSugarGraph";
import UsageLimitModal from "./UsageLimitModal";
import UsageIndicator from "./UsageIndicator";
import { BackIcon } from "./Icons";

interface RecipeAnalysis {
  overallGI: "low" | "medium" | "high";
  content: string;
}

interface RecipeTransformerProps {
  onBack?: () => void;
}

export default function RecipeTransformer({ onBack }: RecipeTransformerProps) {
  const { user } = useAuth();
  const { dietaryPrefs } = useUserStats();
  const { showBadge } = useBadgeToast();
  const [recipe, setRecipe] = useState("");
  const [analysis, setAnalysis] = useState<RecipeAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState<{ currentUsage: number; isPremiumOnly: boolean }>({ currentUsage: 0, isPremiumOnly: false });
  const [usageRefreshKey, setUsageRefreshKey] = useState(0);

  const handleAnalyze = async () => {
    if (!recipe.trim() || !user) return;

    const usageCheck = await checkUsageLimit(user.uid, "recipeTransformer");
    if (!usageCheck.allowed) {
      setLimitInfo({ currentUsage: usageCheck.currentUsage, isPremiumOnly: usageCheck.isPremiumOnly });
      setShowLimitModal(true);
      return;
    }

    setIsAnalyzing(true);
    try {
      const result = await analyzeRecipe(recipe, dietaryPrefs);
      const giMatch = result.match(/OVERALL_GI:(low|medium|high)/i);
      const overallGI = (giMatch?.[1]?.toLowerCase() as "low" | "medium" | "high") || "medium";
      const content = result.replace(/OVERALL_GI:(low|medium|high)/gi, "").trim();
      setAnalysis({ overallGI, content });

      await recordFeatureUsage(user.uid, "recipeTransformer");
      await recordRecipeAnalysis(user.uid);
      setUsageRefreshKey((k) => k + 1);
      const stats = await getUserStats(user.uid);
      const newBadges = await checkAndAwardBadges(user.uid, stats);
      newBadges.forEach((badge) => showBadge(badge));
    } catch (error) {
      console.error("Error analyzing recipe:", error);
      setAnalysis({
        overallGI: "medium",
        content: "Sorry, I had trouble analyzing that recipe. Please try again.",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const sampleRecipes = [
    {
      name: "Classic Banana Bread",
      recipe: `Banana Bread Recipe:
- 3 ripe bananas, mashed
- 1 cup white sugar
- 1/3 cup melted butter
- 1 egg
- 1 teaspoon vanilla
- 1 teaspoon baking soda
- 1.5 cups all-purpose flour`,
    },
    {
      name: "Pasta Carbonara",
      recipe: `Spaghetti Carbonara:
- 400g spaghetti
- 200g pancetta
- 4 egg yolks
- 1 cup parmesan cheese
- Black pepper
- Salt`,
    },
  ];

  return (
    <div className="max-w-2xl mx-auto p-4">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted font-[family-name:--font-ui] mb-4 hover:text-text"
        >
          <BackIcon className="w-4 h-4" /> Back
        </button>
      )}
      <div className="section-label">Recipe Transformer</div>
      <p className="text-sm text-muted mb-6">Paste any recipe to get GI analysis and smart swaps.</p>

      {!analysis ? (
        <>
          <div className="mb-4">
            <textarea
              value={recipe}
              onChange={(e) => setRecipe(e.target.value)}
              placeholder="Paste your recipe here... Include ingredients and quantities for best results."
              rows={8}
              className="border border-border rounded-md bg-bg text-text text-sm px-4 py-3 focus:border-accent outline-none resize-none w-full"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs text-muted font-[family-name:--font-ui]">Try a sample:</span>
            {sampleRecipes.map((sample, i) => (
              <button
                key={i}
                onClick={() => setRecipe(sample.recipe)}
                className="text-accent text-xs font-[family-name:--font-ui] hover:underline"
              >
                {sample.name}
              </button>
            ))}
          </div>

          <button
            onClick={handleAnalyze}
            disabled={!recipe.trim() || isAnalyzing}
            className="w-full py-2.5 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <div className="spinner spinner--sm"></div>
                Analyzing...
              </>
            ) : (
              "Analyze Recipe"
            )}
          </button>
        </>
      ) : (
        <div className="space-y-6">
          <BloodSugarGraph type={analysis.overallGI} label="Expected Blood Sugar Response" />

          <AIContent content={analysis.content} />

          <button
            onClick={() => {
              setAnalysis(null);
              setRecipe("");
            }}
            className="w-full py-2.5 border border-border rounded-md text-text font-[family-name:--font-ui] text-sm hover:bg-bg"
          >
            Analyze Another Recipe
          </button>
        </div>
      )}

      {/* Usage Indicator */}
      <div className="mt-4 flex justify-center">
        <UsageIndicator feature="recipeTransformer" compact onLimitReached={() => setShowLimitModal(true)} refreshKey={usageRefreshKey} />
      </div>

      {/* Usage Limit Modal */}
      <UsageLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        feature="recipeTransformer"
        currentUsage={limitInfo.currentUsage}
        isPremiumOnly={limitInfo.isPremiumOnly}
      />
    </div>
  );
}
